import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';

/**
 * The tints a tile's icon may wear.
 *
 * Solid token pairs rather than an opacity modifier on the accent: a `/10` on a
 * `var()` colour compiles to nothing in this Tailwind setup, so the tint would
 * simply be missing. Both themes come for free — each name resolves to a
 * different value under `[data-theme]`.
 *
 * The four status names keep their product-wide meaning, so a tile drawn `crit`
 * is saying something is wrong, not merely that it wanted to be red.
 */
export const FIGURE_TONE = {
  accent: 'bg-accent-soft text-accent',
  info: 'bg-info-bg text-info',
  pick: 'bg-pick-soft text-pick',
  warn: 'bg-warn-bg text-warn',
  ok: 'bg-ok-bg text-ok',
  crit: 'bg-crit-bg text-crit',
  neutral: 'bg-surface-2 text-ink-2',
} as const;

export type FigureTone = keyof typeof FIGURE_TONE;

export interface FigureTileProps {
  label: string;
  value: ReactNode;
  /** The quiet line under the figure — "Across all grades", "51.4%". */
  hint?: string;
  icon: ReactNode;
  tone?: FigureTone;
  /**
   * Which way round the tile reads.
   *
   * `label-first` puts the caption above the figure with the icon opposite it —
   * for a row where the labels are what is scanned. `figure-first` leads with
   * the icon and the number, for a row where the numbers are.
   *
   * `stacked` reads top to bottom — the icon and the label on one line, the
   * figure under them, the hint under that. For a wide row of five or more,
   * where a tile has no width to put the icon opposite the label and the
   * figures still have to line up across the row.
   */
  layout?: 'label-first' | 'figure-first' | 'stacked';
  isLoading?: boolean;
  className?: string;
  /**
   * Narrows the screen the tile is already on to the rows behind this figure.
   *
   * A tile that only shows a number is a full stop; one that opens the rows it
   * counted is the way in. Given this, the whole panel becomes a button.
   */
  onClick?: () => void;
  /**
   * Where the rows behind this figure live, when they are not on this screen.
   *
   * The other half of `onClick`: some figures have a page behind them and some
   * have a filter. A figure counting something the screen cannot show — the
   * teachers behind a class roster — belongs on the page that lists them, and a
   * tile that merely filtered would be a promise it could not keep.
   */
  to?: string;
  /**
   * Drawn as the filter currently in force, so a reader can see which figure
   * they are looking through. Meaningless without `onClick`.
   */
  isActive?: boolean;
  /**
   * What the click does, read out in place of the tile's contents. Without it a
   * screen reader announces "642, button" — the number without the noun.
   */
  actionLabel?: string;
}

/**
 * One headline figure, in a neutral panel with a tinted icon.
 *
 * Neutral on purpose: five saturated cards in a row read as a paint chart, and
 * the number is what the reader came for. The colour is carried by the icon
 * alone, and never carries meaning by itself — every tile says in words what it
 * is counting.
 *
 * Distinct from `StatCard`, which floods the whole panel with its variant's
 * colour. That one belongs on a dashboard with two or three tiles; this one is
 * for a row of five above a table.
 */
export function FigureTile({
  label,
  value,
  hint,
  icon,
  tone = 'accent',
  layout = 'label-first',
  isLoading = false,
  className,
  onClick,
  to,
  isActive = false,
  actionLabel,
}: FigureTileProps) {
  const mark = (
    <span
      className={cn('grid h-11 w-11 shrink-0 place-items-center rounded-card', FIGURE_TONE[tone])}
      aria-hidden
    >
      {icon}
    </span>
  );

  const body =
    layout === 'stacked' ? (
      <div className="min-w-0 text-left">
        <div className="flex items-center gap-2.5">
          {mark}
          <p className="lbl min-w-0 flex-1 truncate">{label}</p>
        </div>
        {isLoading ? (
          <Skeleton className="mb-1 mt-3 h-8 w-16 rounded-field" />
        ) : (
          <p className="mt-3 text-3xl font-[700] tabular-nums tracking-tight text-ink">{value}</p>
        )}
        {hint && <p className="mt-1 truncate text-xs text-ink-3">{hint}</p>}
      </div>
    ) : layout === 'figure-first' ? (
      <>
        {mark}
        <div className="min-w-0 text-left">
          {isLoading ? (
            <Skeleton className="h-7 w-16 rounded-field" />
          ) : (
            <p className="text-2xl font-[700] tabular-nums tracking-tight text-ink">{value}</p>
          )}
          <p className="mt-0.5 truncate text-sm font-[560] text-ink-2">{label}</p>
          {hint && <p className="mt-0.5 truncate text-xs text-ink-3">{hint}</p>}
        </div>
      </>
    ) : (
      <>
        <div className="min-w-0 text-left">
          <p className="lbl truncate">{label}</p>
          {isLoading ? (
            <Skeleton className="mb-1.5 mt-2 h-7 w-16 rounded-field" />
          ) : (
            <p className="mt-1.5 text-3xl font-[700] tabular-nums tracking-tight text-ink">{value}</p>
          )}
          {hint && <p className="mt-1 truncate text-xs text-ink-3">{hint}</p>}
        </div>
        {mark}
      </>
    );

  const shell = cn(
    'card gap-3 p-4',
    layout === 'stacked'
      ? 'block'
      : layout === 'figure-first'
        ? 'flex items-center'
        : 'flex items-start justify-between',
    className,
  );

  const interactive = cn(
    'w-full cursor-pointer transition-colors hover:border-accent-line',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
  );

  if (to) {
    return (
      <Link to={to} aria-label={actionLabel ?? label} className={cn(shell, interactive)}>
        {body}
      </Link>
    );
  }

  if (!onClick) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      aria-label={actionLabel ?? label}
      className={cn(
        shell,
        interactive,
        // The filter in force reads as pressed, not merely hovered — a border
        // the tile keeps once the pointer has left.
        isActive && 'border-accent bg-accent-soft',
      )}
    >
      {body}
    </button>
  );
}
